/**
 * The map of company settings: what the left navigation of the settings area
 * shows, and which URL each entry lives at.
 */

export type SettingsSectionId =
  | "company"
  | "roles"
  | "permissions"
  | "storage"
  | "signing"
  | "finvalda"
  | "customer-terms"
  | "sales"
  | "incoming-invoices"
  | "hr"
  | "procedures"
  | "business-trips"
  | "fuel"
  | "terminal"
  | "assistant";

interface SettingsEntryText {
  labelKey: string;
  /** One sentence under the page title: why this setting exists. */
  leadKey: string;
  /**
   * Other pages that belong to this entry and keep it highlighted, written
   * like `path` (relative to /app/:companyId/). A longer URL under one of them
   * counts too: "user-merge-history" also covers "user-merge-history/abc".
   */
  alsoActiveFor?: string[];
}

export type SettingsEntry =
  // A part of the general settings screen, shown at settings/<id>.
  | (SettingsEntryText & { kind: "section"; id: SettingsSectionId })
  // A page that already has its own URL (relative to /app/:companyId/); the
  // settings layout only wraps it.
  | (SettingsEntryText & { kind: "page"; path: string });

interface SettingsGroup {
  titleKey: string;
  entries: SettingsEntry[];
}

/**
 * Read top to bottom: groups and entries appear in the navigation in exactly
 * this order. An entry is either a section of the general settings screen
 * (`kind: "section"`) or an existing page that keeps its own URL and is shown
 * inside the same layout (`kind: "page"`).
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    titleKey: "settings_nav_group_company",
    entries: [
      {
        kind: "section",
        id: "company",
        labelKey: "settings_nav_company_label",
        leadKey: "settings_nav_company_lead",
      },
      {
        kind: "section",
        id: "roles",
        labelKey: "settings_nav_roles_label",
        leadKey: "settings_nav_roles_lead",
      },
      {
        kind: "page",
        path: "settings/holidays",
        labelKey: "settings_nav_holidays_label",
        leadKey: "settings_nav_holidays_lead",
      },
    ],
  },
  {
    titleKey: "settings_nav_group_people",
    entries: [
      {
        kind: "page",
        path: "users",
        labelKey: "settings_nav_users_label",
        leadKey: "settings_nav_users_lead",
      },
      {
        kind: "section",
        id: "permissions",
        labelKey: "settings_nav_permissions_label",
        leadKey: "settings_nav_permissions_lead",
      },
    ],
  },
  {
    titleKey: "settings_nav_group_documents",
    entries: [
      {
        kind: "section",
        id: "storage",
        labelKey: "settings_nav_storage_label",
        leadKey: "settings_nav_storage_lead",
      },
      {
        kind: "section",
        id: "signing",
        labelKey: "settings_nav_signing_label",
        leadKey: "settings_nav_signing_lead",
        alsoActiveFor: ["signontab-users"],
      },
      {
        kind: "page",
        path: "files/templates",
        labelKey: "settings_nav_templates_label",
        leadKey: "settings_nav_templates_lead",
      },
      {
        kind: "page",
        path: "series",
        labelKey: "settings_nav_series_label",
        leadKey: "settings_nav_series_lead",
      },
      {
        kind: "page",
        path: "filestypes",
        labelKey: "settings_nav_file_names_label",
        leadKey: "settings_nav_file_names_lead",
      },
      {
        kind: "page",
        path: "customfields",
        labelKey: "settings_nav_custom_fields_label",
        leadKey: "settings_nav_custom_fields_lead",
      },
    ],
  },
  {
    titleKey: "settings_nav_group_finance",
    entries: [
      {
        kind: "section",
        id: "finvalda",
        labelKey: "settings_nav_finvalda_label",
        leadKey: "settings_nav_finvalda_lead",
      },
      {
        kind: "section",
        id: "customer-terms",
        labelKey: "settings_nav_customer_terms_label",
        leadKey: "settings_nav_customer_terms_lead",
      },
      {
        kind: "section",
        id: "sales",
        labelKey: "settings_nav_sales_label",
        leadKey: "settings_nav_sales_lead",
        alsoActiveFor: ["invoice-series"],
      },
      {
        kind: "section",
        id: "incoming-invoices",
        labelKey: "settings_nav_incoming_invoices_label",
        leadKey: "settings_nav_incoming_invoices_lead",
      },
    ],
  },
  {
    titleKey: "settings_nav_group_modules",
    entries: [
      {
        kind: "section",
        id: "hr",
        labelKey: "settings_nav_hr_label",
        leadKey: "settings_nav_hr_lead",
      },
      {
        kind: "section",
        id: "procedures",
        labelKey: "settings_nav_procedures_label",
        leadKey: "settings_nav_procedures_lead",
      },
      {
        kind: "section",
        id: "business-trips",
        labelKey: "settings_nav_business_trips_label",
        leadKey: "settings_nav_business_trips_lead",
      },
      {
        kind: "section",
        id: "fuel",
        labelKey: "settings_nav_fuel_label",
        leadKey: "settings_nav_fuel_lead",
      },
      {
        kind: "page",
        path: "assetstypes",
        labelKey: "settings_nav_asset_types_label",
        leadKey: "settings_nav_asset_types_lead",
      },
      {
        kind: "section",
        id: "terminal",
        labelKey: "settings_nav_terminal_label",
        leadKey: "settings_nav_terminal_lead",
      },
      {
        kind: "section",
        id: "assistant",
        labelKey: "settings_nav_assistant_label",
        leadKey: "settings_nav_assistant_lead",
      },
    ],
  },
  {
    titleKey: "settings_nav_group_tools",
    entries: [
      {
        kind: "page",
        path: "settings/activity-log",
        labelKey: "settings_nav_activity_log_label",
        leadKey: "settings_nav_activity_log_lead",
      },
      {
        kind: "page",
        path: "settings/user-merge",
        labelKey: "settings_nav_user_merge_label",
        leadKey: "settings_nav_user_merge_lead",
        alsoActiveFor: ["user-merge-history"],
      },
      {
        kind: "page",
        path: "settings/customer-form-validation-attempts",
        labelKey: "settings_nav_form_errors_label",
        leadKey: "settings_nav_form_errors_lead",
      },
    ],
  },
];

const ALL_SETTINGS_ENTRIES: SettingsEntry[] = SETTINGS_GROUPS.flatMap(
  (group) => group.entries
);

/** The entry's URL, relative to /app/:companyId/. */
export function settingsEntryPath(entry: SettingsEntry): string {
  return entry.kind === "section" ? `settings/${entry.id}` : entry.path;
}

export function isSettingsSectionId(
  value: string | undefined
): value is SettingsSectionId {
  return ALL_SETTINGS_ENTRIES.some(
    (entry) => entry.kind === "section" && entry.id === value
  );
}

/** "/app/c1/series/5" -> "series/5" */
function pathInsideCompany(pathname: string): string {
  return pathname.replace(/^\/app\/[^/]+\//, "");
}

/** `path` is `base` itself or a page below it ("series" owns "series/5", not "series-x"). */
export function isSameOrBelow(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

/** The entry to highlight for the current URL, or undefined outside the settings area. */
export function findActiveSettingsEntry(
  pathname: string
): SettingsEntry | undefined {
  const path = pathInsideCompany(pathname);
  return ALL_SETTINGS_ENTRIES.find((entry) =>
    [settingsEntryPath(entry), ...(entry.alsoActiveFor ?? [])].some((base) =>
      isSameOrBelow(path, base)
    )
  );
}

/**
 * Every URL (relative to /app/:companyId/) that belongs to the settings area:
 * the sidebar highlights its single «Settings» item on all of them.
 */
export function settingsAreaPaths(): string[] {
  const paths = ["settings"];
  ALL_SETTINGS_ENTRIES.forEach((entry) => {
    if (entry.kind === "page") paths.push(entry.path);
    paths.push(...(entry.alsoActiveFor ?? []));
  });
  return paths;
}

/**
 * Old links looked like settings?tab=<tab>&ref=<ref>: the tab id and a numeric
 * reference code ("1.2") for the same screen. Every row says where that old
 * screen lives now. Read by `legacySettingsTarget`, which uses the first row
 * whose `ref` matches the link's ref, otherwise the first row whose `tab`
 * matches the link's tab (so ref wins over tab, as before), and falls back to
 * "company" when nothing matches.
 *
 * Several old screens may now share one section (Microsoft Connect and
 * Document storage both live in "storage"). `sub` is the inner tab of the new
 * section that the old screen became, added to the link as ?sub=<sub>.
 */
export const LEGACY_SETTINGS_LINKS: {
  tab: string;
  ref: string | null;
  section: SettingsSectionId;
  sub?: string;
}[] = [
  { tab: "company-info", ref: "1", section: "company" },
  { tab: "finvalda", ref: "1.1", section: "finvalda" },
  { tab: "microsoft-connect", ref: "1.2", section: "storage" },
  { tab: "document-storage", ref: "1.5", section: "storage" },
  { tab: "dokobit-connect", ref: "1.3", section: "signing" },
  { tab: "signontab-connect", ref: "1.4", section: "signing" },
  { tab: "finance-settings", ref: "2", section: "customer-terms" },
  { tab: "sanctions-settings", ref: "2.1", section: "customer-terms" },
  { tab: "vat-check-settings", ref: "2.3", section: "customer-terms" },
  { tab: "onboarding", ref: "3", section: "hr" },
  { tab: "terminal-delivery", ref: "5", section: "terminal" },
  { tab: "work-security", ref: "6", section: "procedures" },
  { tab: "business-trip", ref: "7", section: "business-trips" },
  { tab: "orders", ref: "8", section: "sales" },
  // The old amendment tab never had a reference code.
  { tab: "order-amendment", ref: null, section: "sales", sub: "order-amendment" },
  { tab: "fuel-report", ref: "9", section: "fuel" },
  { tab: "assistant", ref: "10", section: "assistant" },
];

/**
 * Where an old settings?tab=...&ref=... link goes now: the section, and the
 * query string to keep (every parameter except tab and ref, e.g. subTab or the
 * googleConnected marker the Google sign-in sends back), starting with "?" or empty.
 */
export function legacySettingsTarget(params: URLSearchParams): {
  section: SettingsSectionId;
  search: string;
} {
  const tab = params.get("tab")?.trim();
  const ref = params.get("ref")?.trim();
  const rowByRef = LEGACY_SETTINGS_LINKS.find(
    (row) => row.ref !== null && row.ref === ref
  );
  const rowByTab = LEGACY_SETTINGS_LINKS.find((row) => row.tab === tab);
  const row = rowByRef ?? rowByTab;

  const kept = new URLSearchParams(params);
  kept.delete("tab");
  kept.delete("ref");
  // The inner tab only applies when the link still lands on the same section.
  if (rowByTab?.sub && rowByTab.section === row?.section && !kept.has("sub")) {
    kept.set("sub", rowByTab.sub);
  }

  const query = kept.toString();
  return {
    section: row?.section ?? "company",
    search: query ? `?${query}` : "",
  };
}
